import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';
import * as yup from 'yup';
import { useForm, yupResolver } from '@mantine/form';

import { Button, Checkbox } from 'ui';

import { useAccount } from '../../store/account/hooks';
import appSettings from '../../app-settings';

const ACCOUNTS_URL = `${appSettings.baseUrl}/accounts`;

const formValidation = yup.object({
  agreeToTerms: yup.boolean().oneOf([true], 'You must agree to the terms.'),
});

const initialValues = {
  agreeToTerms: false,
};

const PaymentForm = ({ children, onSuccess }) => {
  const form = useForm({
    initialValues,
    validate: yupResolver(formValidation),
    validateInputOnBlur: true,
  });
  const stripe = useStripe();
  const elements = useElements();
  const { account } = useAccount();
  const [subscription, setSubscription] = useState({ items: {} });
  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (account) {
      setSubscription(account.subscription);
    }
  }, [account]);

  const handleError = (error) => {
    setLoading(false);
    setErrorMessage(error.message);
  };

  const handleSubmit = async (event) => {
    if (!stripe) return;

    setLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    const { error, confirmationToken } = await stripe.createConfirmationToken({
      elements,
    });

    if (error) {
      handleError(error);
      return;
    }

    const response = await axios.post(`${ACCOUNTS_URL}/subscription`, {
      confirmationTokenId: confirmationToken.id,
      subscription,
    });

    console.log('response', response);

    setLoading(false);
    onSuccess();
  };

  return (
    <form
      noValidate
      className="flex w-full flex-col items-center justify-center"
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <div className="flex flex-row gap-12">
        <PaymentElement options={{ layout: { type: 'tabs' }, readOnly: loading }} className="w-[400px]" />

        <div className="w-[300px] space-y-4 text-base">
          {children}

          <Checkbox
            id="agree-to-terms"
            label={
              <label htmlFor="agree-to-terms" className="text-base">
                By subscribing, you agree to our{' '}
                <a
                  href="https://www.donorspring.com/legal"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-700"
                >
                  Product & Services Specific Terms
                </a>{' '}
                and{' '}
                <a href="https://www.donorspring.com/msa" target="_blank" rel="noreferrer" className="text-primary-700">
                  MSA
                </a>
              </label>
            }
            checked={form.values.agreeToTerms}
            onChange={(e) => form.setFieldValue('agreeToTerms', e.target.checked)}
            error={!!form.errors.agreeToTerms}
            helperText={form.errors.agreeToTerms}
          />

          <Button
            title="Start Subscription"
            color="primary"
            size="lg"
            className="!block w-full"
            type="submit"
            loading={loading}
          />
        </div>
      </div>
    </form>
  );
};

PaymentForm.propTypes = {
  children: PropTypes.node.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default PaymentForm;
